import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import routes from "@/routes/routes";
import styles from "./Navbar.module.scss";
import { usePermissions } from "@/hooks/usePermissions";
import { FaUserCircle } from "react-icons/fa";
import {
  Checkin,
  checkout,
  getUsersSetting,
} from "@/store/slices/userSlice";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "@/store/store";
export interface SelectOption {
  label: string;
  value: string;
}
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { motion, AnimatePresence } from "framer-motion";
import ProfileDropdown from "@/components/User/profile-dropdown/ProfileDropdown";
import PunchInReportModal from "@/components/User/user-punch/PunchInReportModal";
import toast from "@/lib/toast";
import { Button, Tooltip } from "@mui/material";
import { getAddress } from "@/store/slices/customerSlice";
import { useLocationPermission } from "@/hooks/useLocationPermission";
// import { PlatformSwitcherTrigger } from "@/components/PlatformSwitcher";
import { ButtonLoader } from "@/components/Loader/ButtonLoader";

dayjs.extend(customParseFormat);

interface NavbarProps {
  showPlatformSwitcherIcon?: boolean;
  onOpenPlatformSwitcher?: () => void;
}

interface userStore {
  CheckInTime?: string;
  CheckOutTime?: string;
}

const Navbar: React.FC<NavbarProps> = ({
  showPlatformSwitcherIcon = false,
  onOpenPlatformSwitcher,
}) => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const userStore = useSelector((store: any) => store.user);

  const { currentUser, permissions, permissionLoading } = usePermissions();
  const { getLocation } = useLocationPermission();

  const [totalHours, setTotalHours] = useState<string>("--:--");
  const timerRef = useRef<number | null>(null);

  const [disablePunchIn, setDisablePunchIn] = useState(false);
  const [disablePunchOut, setDisablePunchOut] = useState(false);

  const [currentTime, setCurrentTime] = useState(
    dayjs().format("DD-MM-YYYY HH:mm:ss")
  );

  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [punchType, setPunchType] = useState<"in" | "out">("in");

  const isUserReady = !!currentUser?.userId && !permissionLoading;

  const setting = userStore?.setting?.[0];

  const pageName = useMemo(() => {
    const currentPath = location.pathname.replace(/\/+$/, "") || "/";

    if (currentPath === "/dashboard") return "Dashboard";
    if (currentPath === "/dashboard360") return "Dashboard 360";

    const buildRoutes = (routesObj: any, parentPath = ""): any[] => {
      const result: any[] = [];

      Object.values(routesObj).forEach((route: any) => {
        const fullPath = `${parentPath}/${route.path || ""}`
          .replace(/\/+/g, "/")
          .replace(/\/$/, "") || "/";

        if (route.pageName) {
          result.push({
            fullPath,
            pageName: route.pageName,
          });
        }

        if (route.children) {
          result.push(...buildRoutes(route.children, fullPath));
        }
      });

      return result;
    };

    const flattenedRoutes = buildRoutes(routes.privateRoutes);

    const sortedRoutes = flattenedRoutes.sort(
      (a, b) => b.fullPath.length - a.fullPath.length
    );

    const match = sortedRoutes.find((route) => {
      if (route.fullPath === currentPath) return true;

      const routeBase = route.fullPath.split("/:")[0];
      return currentPath.startsWith(routeBase);
    });

    return match?.pageName || "";
  }, [location.pathname, routes]);

  const formattedTime = (value?: string) =>
    value
      ? dayjs(value, "MM-DD-YYYY hh:mm:ss A").format("hh:mm A")
      : "--:--";

  const calculateTotalHours = (endStr: string, startStr: string) => {
    const format = "MM-DD-YYYY hh:mm:ss A";

    const start = dayjs(startStr.trim(), format, true);
    const end = dayjs(endStr.trim(), format, true);

    if (!start.isValid() || !end.isValid()) return "--:--";

    let diffMs = end.diff(start);

    const isNegative = diffMs < 0;
    diffMs = Math.abs(diffMs);

    const hrs = Math.floor(diffMs / (1000 * 60 * 60));
    const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${isNegative ? "-" : ""}${hrs} Hrs ${mins} mins`;
  };

  const startTimer = (checkInStr: string) => {
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      const nowStr = dayjs().format("MM-DD-YYYY hh:mm:ss A");
      setTotalHours(calculateTotalHours(nowStr, checkInStr));
    }, 60000);
  };

  const pauseTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
  };

  useEffect(() => {
    if (setting?.CheckInTime && !setting?.CheckOutTime) {
      setTotalHours(
        calculateTotalHours(
          dayjs().format("MM-DD-YYYY hh:mm:ss A"),
          setting.CheckInTime
        )
      );
      startTimer(setting.CheckInTime);
    } else if (setting?.CheckInTime && setting?.CheckOutTime) {
      setTotalHours(
        calculateTotalHours(setting.CheckOutTime, setting.CheckInTime)
      );
      pauseTimer();
    }

    return () => pauseTimer();
  }, [setting]);

  const refreshSettings = () => {
    if (currentUser?.userId) {
      dispatch(getUsersSetting(currentUser.userId));
    }
  };

  useEffect(() => {
    if (isUserReady) {
      refreshSettings();
    }
  }, [isUserReady]);

  const handlePunchIn = async (report?: string, workstationId?: string) => {
    try {
      setDisablePunchIn(true);

      const { lat, lng } = await getLocation();

      const address = await dispatch(
        getAddress({ latitude: lat, longitude: lng })
      ).unwrap();

      await dispatch(
        Checkin({
          CheckInLatitude: lat,
          CheckInLongitude: lng,
          CheckInLocation: address,
          PunchInReport: report || "",
          WorkstationId: workstationId || null,
        })
      ).unwrap();

      toast.success("Punch In successful!");
      refreshSettings();
      setModalOpen(false);
    } catch (err: any) {
      toast.error(err);
    } finally {
      setDisablePunchIn(false);
    }
  };

  const handlePunchOut = async (report?: string) => {
    try {
      setDisablePunchOut(true);

      const { lat, lng } = await getLocation();

      const address = await dispatch(
        getAddress({ latitude: lat, longitude: lng })
      ).unwrap();

      await dispatch(
        checkout({
          CheckOutLatitude: lat,
          CheckOutLongitude: lng,
          CheckOutLocation: address,
          Report: report || "",
        })
      ).unwrap();

      toast.success("Punch Out successful!");
      refreshSettings();
      setModalOpen(false);
    } catch (err: any) {
      toast.error(err);
    } finally {
      setDisablePunchOut(false);
    }
  };

  const onPunchInClick = () => {
    if (setting?.IsPunchInReport || setting?.IsWorkstation) {
      setPunchType("in");
      setModalOpen(true);
    } else {
      handlePunchIn();
    }
  };

  const onPunchOutClick = () => {
    setPunchType("out");
    setModalOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !buttonRef.current?.contains(event.target as Node) &&
        !dropdownRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const updateTime = () => {
      setCurrentTime(dayjs().format("MMM D, YYYY"));
    };
    updateTime();
    const interval = setInterval(updateTime, 3600000);
    return () => clearInterval(interval);
  }, []);

  if (!isUserReady) return null;

  return (
    <div className={styles.topBarContainer}>
      <div className={styles.leftSide}>
        <p className={styles.pageName}>{pageName}</p>
      </div>

      <div className={styles.rightSide}>
        <div className={styles.timeLineSection}>
          {!setting?.CheckInTime && (
            <div className="flex gap-4 items-center text-gray-600 text-sm font-medium">
              <span className="border-r pr-4">
                {dayjs().format("hh:mm A")}
              </span>
              <span>{currentTime}</span>
            </div>
          )}

          {setting?.CheckInTime && (
            <Tooltip title="Punched In">
              <span className={styles.timeLine}>
                {formattedTime(setting.CheckInTime)}
              </span>
            </Tooltip>
          )}

          {setting?.CheckOutTime && (
            <Tooltip title="Punched Out">
              <span className={`${styles.timeLine} text-red-500`}>
                {formattedTime(setting.CheckOutTime)}
              </span>
            </Tooltip>
          )}

          {setting?.CheckInTime && (
            <Tooltip title="Total Hours">
              <span className={styles.timeLine}>{totalHours}</span>
            </Tooltip>
          )}

          {setting?.CheckInTime === "" && permissions?.includes("PunchIn_PunchOut") && (
            <Tooltip
              title={!setting?.IsWebPunchIn ? "Your Punch In is disabled" : ""}
            >
              <span>
                <Button
                  disabled={disablePunchIn || !setting?.IsWebPunchIn}
                  onClick={onPunchInClick}
                  variant="contained"
                >
                  Punch In
                </Button>
              </span>
            </Tooltip>
          )}

          {setting?.CheckInTime !== "" && setting?.CheckOutTime === "" && permissions?.includes("PunchIn_PunchOut") && (
            <Tooltip
              title={!setting?.IsWebPunchOut ? "Your Punch Out is disabled" : ""}
            >
              <span>
                <ButtonLoader
                  disabled={disablePunchOut || !setting?.IsWebPunchOut}
                  onClick={onPunchOutClick}
                  variant="contained"
                >
                  Punch Out
                </ButtonLoader>
              </span>
            </Tooltip>
          )}
        </div>

        {/* user */}
        <div className={styles.userSection}>
          {/* {showPlatformSwitcherIcon && onOpenPlatformSwitcher && (
            <PlatformSwitcherTrigger onClick={onOpenPlatformSwitcher} />
          )} */}
          <button
            ref={buttonRef}
            className={`${styles.userName} ${isOpen ? styles.active : ""}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <span className="text-gray-500">{currentUser?.userDetail?.data?.Name}</span>
            <FaUserCircle fontSize={"28px"} className="text-gray-500" />
          </button>

          <AnimatePresence>
            {isOpen && (
              <motion.div
                ref={dropdownRef}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 50 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="absolute right-3 top-full mt-2 rounded-[15px] shadow-2xl z-50 bg-white"
                onClick={(e) => e.stopPropagation()}
              >
                <ProfileDropdown />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Modals */}
        <PunchInReportModal
          modalOpen={modalOpen}
          closeModal={() => setModalOpen(false)}
          onSubmit={(report, workstation) => {
            if (punchType === "in") {
              handlePunchIn(report, workstation);
            } else {
              handlePunchOut(report);
            }
          }}
          isPunchReport={
            punchType === "out" ? true : setting?.IsPunchInReport
          }
          isWorkstation={
            punchType === "out" ? false : setting?.IsWorkstation
          }
          isLoading={punchType === "in" ? disablePunchIn : disablePunchOut}
        />
      </div>
    </div>
  );
};

export default memo(Navbar);
